import { UploadToast } from "@/ui/upload-toast";
import { UploadProgressContext } from "@/contexts/UploadProgressContext";
import { env } from "@/env";
import type { SessionData } from "@/utils/getSession";
import { useContext, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import retry from "async-retry";
import { createId } from "@paralleldrive/cuid2";

export type UploadFileResponse = {
	id: string;
	globalId: string;
	type: string;
	createdBy: string;
	bucket: {
		id: string;
		name: string;
	};
	name: string;
	mimeType: string;
	hash?: string;
	size: string;
	status: string;
	visibility: string;
	muxId?: string;
	muxPlaybackId?: string;
	createdAt: string;
	updated: string;
	url: string;
};

export async function uploadFile({
	uploadId,
	file,
	club,
	session,
	onUploadProgress,
}: {
	uploadId: string;
	file: File;
	club?: string;
	session?: Partial<SessionData>;
	onUploadProgress?: ({
		uploadId,
		progress,
	}: {
		uploadId: string;
		progress: number;
	}) => void;
}) {
	onUploadProgress?.({ uploadId, progress: 0 });

	let uploadToast: string | number | undefined;

	if (uploadId) {
		uploadToast = toast(<UploadToast uploadId={uploadId} />, {
			dismissible: false,
			duration: Infinity,
		});
	}

	try {
		const {
			data: { id, url },
		} = await axios.post<{ id: string; name: string; url: string }>(
			`${env.VITE_SERVER_URL}/storage/signed-url`,
			{
				fileName: file.name,
				contentType: file.type,
				club,
				size: file.size.toString(),
				visibility: "private",
			},
			{
				headers: {
					...(session?.accessToken
						? { Authorization: `Bearer ${session?.accessToken}` }
						: undefined),
				},
			},
		);

		await retry(
			() =>
				axios.put(url, file, {
					headers: {
						"Content-Type": file.type,
					},
					onUploadProgress: onUploadProgress
						? (event: { progress?: number }) =>
								onUploadProgress({ uploadId, progress: event.progress || 0 })
						: () => null,
				}),
			{ retries: 3 },
		);

		onUploadProgress?.({ uploadId, progress: 1 });

		const { data: fileData } = await axios.post<UploadFileResponse>(
			`${env.VITE_SERVER_URL}/storage/commit/${id}`,
		);

		onUploadProgress?.({ uploadId, progress: 0 });

		return fileData;
	} catch (err) {
		toast.error(
			"Ocorreu um erro ao enviar o arquivo. Tente novamente mais tarde.",
		);
		throw err;
	} finally {
		if (uploadToast) {
			toast.dismiss(uploadToast);
		}

		onUploadProgress?.({ uploadId, progress: 0 });
	}
}

export async function multipartUpload({
	uploadId,
	file,
	club,
	session,
	onUploadProgress,
}: {
	uploadId: string;
	file: File;
	club?: string;
	session?: Partial<SessionData>;
	onUploadProgress?: ({
		uploadId,
		progress,
	}: {
		uploadId: string;
		progress: number;
	}) => void;
}) {
	onUploadProgress?.({ uploadId, progress: 0 });

	const CHUNK_SIZE = 10 * 1024 * 1024; // 5MB
	const totalParts = Math.ceil(file.size / CHUNK_SIZE);

	let uploadToast: string | number | undefined;

	if (uploadId) {
		uploadToast = toast(<UploadToast uploadId={uploadId} />, {
			dismissible: false,
			duration: Infinity,
		});
	}

	try {
		const {
			data: { uploadId, uploadKey, id: fileID },
		} = await axios.post<{ uploadId: string; uploadKey: string; id: string }>(
			`${env.VITE_SERVER_URL}/storage/multiupload-create`,
			{
				fileName: file.name, //.replaceAll(" ", "_"),
				contentType: file.type,
				club,
				size: file.size.toString(),
				visibility: "private",
			},
			{
				headers: {
					...(session?.accessToken
						? { Authorization: `Bearer ${session?.accessToken}` }
						: undefined),
				},
			},
		);

		const parts: { PartNumber: number; ETag: string }[] = [];

		for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
			const start = (partNumber - 1) * CHUNK_SIZE;
			const end = Math.min(start + CHUNK_SIZE, file.size);
			const blob = file.slice(start, end);

			// Step 2: Get signed URL for each part
			const {
				data: { url },
			} = await retry(
				() => {
					return axios.post<{ url: string }>(
						`${env.VITE_SERVER_URL}/storage/multiupload-url`,
						{
							key: uploadKey,
							uploadId,
							partNumber,
						},
					);
				},
				{ retries: 3 },
			);

			// Step 3: Upload part to S3
			const { headers } = await retry(
				() => {
					return axios.put(url, blob, {
						headers: {
							"Content-Type": file.type,
						},
					});
				},
				{ retries: 3 },
			);

			const etag = headers.etag?.replace(/"/g, "");
			parts.push({ PartNumber: partNumber, ETag: etag });

			onUploadProgress?.({ uploadId, progress: partNumber / totalParts });
		}

		const { data: response } = await retry(() =>
			axios.post<UploadFileResponse>(
				`${env.VITE_SERVER_URL}/storage/multiupload-commit/${fileID}`,
				{
					parts,
					key: uploadKey,
					uploadId,
				},
			),
		);

		return response;
	} catch (err) {
		toast.error(
			"Ocorreu um erro ao enviar o arquivo. Tente novamente mais tarde.",
		);
		throw err;
	} finally {
		if (uploadToast) {
			toast.dismiss(uploadToast);
		}

		onUploadProgress?.({ uploadId, progress: 0 });
	}
}

export function useFileUpload({
	club,
	uploadId: _uploadId = createId(),
	session,
}: {
	club: string;
	uploadId?: string;
	session?: Partial<SessionData>;
}) {
	const [uploadId] = useState(_uploadId);
	const { setProgress } = useContext(UploadProgressContext);

	const _multipartUpload = (file: File) => {
		setProgress(uploadId, 0);

		return multipartUpload({
			session,
			file,
			club,
			uploadId,
			onUploadProgress: ({ progress }) => {
				setProgress(uploadId, progress);
			},
		});
	};

	const _regularUpload = (file: File) => {
		setProgress(uploadId, 0);

		return uploadFile({
			session,
			file,
			club,
			uploadId,
			onUploadProgress: ({ progress }) => setProgress(uploadId, progress),
		});
	};

	const upload = (file: File) => {
		if (file.size > 500 * 1024 * 1024) {
			return _multipartUpload(file);
		}

		return _regularUpload(file);
	};

	return { upload };
}
